/**
 * Local Block Definitions
 *
 * Defines the 8 implemented block types locally instead of fetching from backend.
 * These are the only block types with custom property panels implemented.
 */

import type { BlockDefinition, EmailBlockCategory } from "@/types/block-definitions";

export const LOCAL_BLOCK_DEFINITIONS: BlockDefinition[] = [
  // Layout Blocks
  {
    blockType: "section",
    displayName: "Section",
    description: "Container for organizing content with background and layout options",
    category: "Layout",
    iconUrl: "layout",
  },
  {
    blockType: "column",
    displayName: "Column",
    description: "Column within a section for multi-column layouts",
    category: "Layout",
    iconUrl: "columns",
  },
  {
    blockType: "group",
    displayName: "Group",
    description: "Holds columns side by side that don't stack on mobile",
    category: "Layout",
    iconUrl: "group",
  },
  {
    blockType: "hero",
    displayName: "Hero",
    description: "Full-width hero section with title, subtitle, and call-to-action",
    category: "Layout",
    iconUrl: "star",
  },
  {
    blockType: "footer",
    displayName: "Footer",
    description: "Email footer with company info, links, social icons, and unsubscribe",
    category: "Layout",
    iconUrl: "footer",
  },

  // Content Blocks
  {
    blockType: "text",
    displayName: "Text",
    description: "Rich text content with formatting options",
    category: "Content",
    iconUrl: "type",
  },
  {
    blockType: "heading",
    displayName: "Heading",
    description: "Heading text with customizable level (H1-H6)",
    category: "Content",
    iconUrl: "heading",
  },
  {
    blockType: "button",
    displayName: "Button",
    description: "Call-to-action button with link",
    category: "Content",
    iconUrl: "mouse-pointer",
  },
  {
    blockType: "quote",
    displayName: "Quote",
    description: "Styled blockquote with optional author and source",
    category: "Content",
    iconUrl: "quote",
  },
  {
    blockType: "menu",
    displayName: "Menu",
    description: "Navigation menu with horizontal or vertical layout",
    category: "Content",
    iconUrl: "menu",
  },
  {
    blockType: "html",
    displayName: "HTML",
    description: "Raw HTML content for advanced customization",
    category: "Content",
    iconUrl: "code",
  },

  // Media Blocks
  {
    blockType: "image",
    displayName: "Image",
    description: "Image with optional link and responsive options",
    category: "Media",
    iconUrl: "image",
  },
  {
    blockType: "video",
    displayName: "Video",
    description: "Video with thumbnail fallback for email clients",
    category: "Media",
    iconUrl: "play",
  },
  {
    blockType: "social",
    displayName: "Social",
    description: "Social media icons with links",
    category: "Media",
    iconUrl: "share-2",
  },

  // Advanced Blocks
  {
    blockType: "divider",
    displayName: "Divider",
    description: "Horizontal line for visual separation",
    category: "Advanced",
    iconUrl: "minus",
  },
  {
    blockType: "spacer",
    displayName: "Spacer",
    description: "Vertical spacing block",
    category: "Advanced",
    iconUrl: "move-vertical",
  },
];

/**
 * Get block definitions by category
 */
export function getLocalBlocksByCategory(category: EmailBlockCategory): BlockDefinition[] {
  return LOCAL_BLOCK_DEFINITIONS.filter(block => block.category === category);
}

/**
 * Get a specific block definition by type
 */
export function getLocalBlockDefinition(blockType: string): BlockDefinition | undefined {
  return LOCAL_BLOCK_DEFINITIONS.find(block => block.blockType === blockType);
}

/**
 * Get all available categories that have blocks
 */
export function getLocalCategories(): EmailBlockCategory[] {
  const categories = new Set<EmailBlockCategory>();
  LOCAL_BLOCK_DEFINITIONS.forEach(block => categories.add(block.category));
  return Array.from(categories);
}
